/**
 * WS-HF — ONE row shape for the Usage page, from two very different sources.
 *
 * The page reads the operator's Higgsfield account directly when the desktop app is connected
 * over MCP, and this app's own log otherwise. The two answer different questions:
 *
 *  - **The Higgsfield account** is the truth about the money. It sees every generation the
 *    operator ran, including the ones they ran in Higgsfield's own app, and its credit figures
 *    are the vendor's. It cannot see a job this app ran on the PLATFORM's key.
 *  - **This app's log** sees only what this app ran, but sees it per job, with a status.
 *
 * Neither is a superset, so the page says which one it is showing rather than blending them.
 * What it must NOT do is shape them differently: a table whose columns mean one thing on a
 * laptop and another on the web is a table nobody can read twice. Hence one `UsageRow`, one
 * mapper per source, and every KPI computed from the rows rather than from the source.
 */
import type { HfMcpTransaction } from "@genz/platform";
import type { HiggsfieldUsageItem, HiggsfieldUsageShare } from "@genz/platform";

/** Which account the numbers came from. */
export type UsageSource = "higgsfield-mcp" | "cloud";

/** The caption under the header. It names the ACCOUNT, because that is what differs. */
export const SOURCE_CAPTIONS: Record<UsageSource, string> = {
  "higgsfield-mcp": "Source: Higgsfield account",
  cloud: "Source: this app's log",
};

export interface UsageRow {
  /** Stable within one page of results — the table's key. */
  id: string;
  /** ISO timestamp, or `undefined` when the source did not send one. */
  at?: string;
  /**
   * Credits, as a POSITIVE spend.
   *
   * Higgsfield's transaction feed signs a spend negative and a top-up positive. Flipping the
   * sign here rather than at each call site is what keeps a "Credits spent" tile from summing
   * to a negative number, and it is why `isSpend` exists: a top-up is not a generation and must
   * not be counted as one.
   */
  credits: number;
  /** True when this row took credits away. A top-up or a refund is false. */
  isSpend: boolean;
  /** What was used — the model on the cloud path, the vendor's display name on the MCP one. */
  feature: string;
  /** The verb, or the job's outcome. Empty when the source sent none. */
  action: string;
  /** USD, when the source knows it. The MCP feed does not — the page prices it locally. */
  costUsd?: number;
}

/** One page of this app's own log. `credits` is the provider's own figure, never derived. */
export function rowsFromCloud(items: HiggsfieldUsageItem[]): UsageRow[] {
  return items.map((it, i) => ({
    id: `${it.at}-${it.model}-${i}`,
    at: it.at,
    credits: Math.abs(it.credits ?? 0),
    // A refused or failed job spent nothing; counting it as a generation inflates every tile.
    isSpend: (it.credits ?? 0) !== 0,
    feature: it.model || "—",
    action: it.status || "",
    costUsd: it.cost_usd ?? undefined,
  }));
}

/**
 * One page of the operator's Higgsfield transaction feed.
 *
 * `usdPerCredit` prices the row locally because the vendor's feed carries credits only. That is
 * the operator's own setting, which is exactly why it is applied HERE and not folded into
 * `credits`: the credit figure stays the vendor's, and only the money moves when the price does.
 */
export function rowsFromMcp(items: HfMcpTransaction[], usdPerCredit: number): UsageRow[] {
  return items.map((t, i) => {
    const spend = t.credits < 0;
    const credits = Math.abs(t.credits);
    return {
      id: `${t.createdAt ?? "?"}-${t.displayName}-${i}`,
      at: t.createdAt,
      credits,
      isSpend: spend,
      feature: t.displayName || "—",
      action: t.action ?? "",
      costUsd: spend ? credits * usdPerCredit : undefined,
    };
  });
}

export interface UsageKpis {
  totalCostUsd: number;
  creditsSpent: number;
  featuresUsed: number;
  totalGenerations: number;
}

/**
 * The four tiles, computed from the rows on screen.
 *
 * Only SPENDS count. A top-up in the feed is money going the other way, and adding it to
 * "Credits spent" would make the tile read as the opposite of what it says.
 */
export function kpisFromRows(rows: UsageRow[], usdPerCredit: number): UsageKpis {
  const spends = rows.filter((r) => r.isSpend);
  return {
    totalCostUsd: spends.reduce((sum, r) => sum + (r.costUsd ?? r.credits * usdPerCredit), 0),
    creditsSpent: spends.reduce((sum, r) => sum + r.credits, 0),
    featuresUsed: new Set(spends.map((r) => r.feature)).size,
    totalGenerations: spends.length,
  };
}

/**
 * The share bar, computed from the rows — descending, so the legend reads top-down.
 *
 * A window that spent nothing answers an EMPTY list, not a row of `NaN%`. Dividing by a zero
 * total is the whole reason this is a function rather than four lines at the call site.
 */
export function shareFromRows(rows: UsageRow[]): HiggsfieldUsageShare[] {
  const spends = rows.filter((r) => r.isSpend);
  const total = spends.reduce((sum, r) => sum + r.credits, 0);
  if (total <= 0) return [];
  const byModel = new Map<string, number>();
  for (const r of spends) byModel.set(r.feature, (byModel.get(r.feature) ?? 0) + r.credits);
  return [...byModel.entries()]
    .map(([model, credits]) => ({ model, credits, pct: (credits / total) * 100 }))
    .sort((a, b) => b.credits - a.credits || a.model.localeCompare(b.model));
}

/** The distinct values a filter dropdown offers, sorted, with blanks dropped. */
export function distinct(rows: UsageRow[], pick: (r: UsageRow) => string): string[] {
  return [...new Set(rows.map(pick).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** `Date` at UTC midnight, `n` whole days before today. */
export function daysAgoIso(n: number, now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/**
 * The ISO date one day after `iso`.
 *
 * The cloud usage endpoint reads `to` as an INSTANT (`created_at <= to`), and a bare date is
 * midnight at the START of that day — so the day the operator sees as the end of the window
 * has to be sent as the day after it, or every generation made on it is dropped.
 */
export function dayAfterIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Keep the rows inside `[from, to]`, both inclusive ISO dates.
 *
 * Applied to the MCP feed ONLY: the cloud endpoint takes the window as a query and answers a
 * page that already obeys it, and filtering a server-paged result a second time would silently
 * shorten pages. A row with NO timestamp is KEPT — dropping it would hide a spend because the
 * vendor omitted a field.
 */
export function withinRange(rows: UsageRow[], from?: string, to?: string): UsageRow[] {
  if (!from && !to) return rows;
  return rows.filter((r) => {
    if (!r.at) return true;
    const day = r.at.slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  });
}
